import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import { buildNavigationUrl } from '../../utils/navigation';
import { FLOW_SLUGS, STEP_DEFINITIONS } from '../../constants';
import { useByocNavigationContext } from '../../context/byoc-navigation-context';
import useIsAfterReduxHydration from '../../../../hooks/use-is-after-redux-hydration';
import type { RootState } from '../../../../reducers';

type StepGuardProps = {
  children: JSX.Element;
};

// Guards against users skipping ahead in the flow (e.g. deep-linking).
// If the current step's guard fails, walks backwards to find the last
// valid step and redirects there in a single hop. Invalid slugs 404 at the page level.
const StepGuard = ({ children }: StepGuardProps) => {
  const isAfterReduxHydration = useIsAfterReduxHydration();
  const { activeSlug: step, journeyType } = useByocNavigationContext();
  const router = useRouter();
  const hasRedirected = useRef(false);

  const redirectUrl = useSelector((state: RootState) => {
    const flow = FLOW_SLUGS[journeyType];
    const stepIndex = flow.indexOf(step);
    const guard = STEP_DEFINITIONS[step]?.entryGuard;
    if (!guard || guard(state, journeyType)) return null;

    for (let i = stepIndex - 1; i >= 0; i -= 1) {
      const slug = flow[i]!;
      const previousGuard = STEP_DEFINITIONS[slug]?.entryGuard;
      if (!previousGuard || previousGuard(state, journeyType)) {
        return buildNavigationUrl(slug, journeyType);
      }
    }

    return null;
  });

  useEffect(() => {
    if (isAfterReduxHydration && redirectUrl && !hasRedirected.current) {
      hasRedirected.current = true;
      router.replace(redirectUrl);
    }
  }, [isAfterReduxHydration, redirectUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  if (redirectUrl) {
    return null;
  }

  return children;
};

export default StepGuard;
